import { Card, SLabel, ICell } from '../components/Atoms';
import { C } from '../constants';
import { toDay, fmtDay } from '../utils';

// Returns [{med, allergen}] for any active med whose name matches a documented allergy
function getAllergyConflicts(allergiesText, activeMeds) {
  if (!allergiesText || !activeMeds || activeMeds.length === 0) return [];
  const allergens = allergiesText
    .split(/[,;\n]+/)
    .map(a => a.trim().toLowerCase())
    .filter(Boolean);
  const conflicts = [];
  activeMeds.forEach(m => {
    const medName = (m.name || '').toLowerCase();
    if (!medName) return;
    allergens.forEach(allergen => {
      if (allergen && (medName.includes(allergen) || allergen.includes(medName))) {
        conflicts.push({ med: m.name, allergen: allergen });
      }
    });
  });
  return conflicts;
}

export default function MedsTab({ cur, patch, setFld }) {
  const medsList   = cur.medsList  || [];
  const ivList     = cur.ivList    || [];
  const dietList   = cur.dietList  || [];
  const activeMeds = medsList.filter(m => !m.dc);
  const dcMeds     = medsList.filter(m => m.dc);
  const activeIV   = ivList.filter(f => !f.dc);
  const dcIV       = ivList.filter(f => f.dc);
  const activeDiet = dietList.filter(d => !d.dc);
  const dcDiet     = dietList.filter(d => d.dc);

  // Medications
  const addMed    = () => patch(p => ({ ...p, medsList: [...(p.medsList || []), { id: `m${Date.now()}`, name: '', dose: '', route: '', freq: '', startDate: toDay(), addedAt: Date.now(), abxTotal: '', dc: false, dcDate: '', dcNote: '' }] }));
  const editMed   = (id, f, v) => patch(p => ({ ...p, medsList: (p.medsList || []).map(m => m.id === id ? { ...m, [f]: v } : m) }));
  const toggleDC  = id => patch(p => ({ ...p, medsList: (p.medsList || []).map(m => m.id === id ? { ...m, dc: !m.dc, dcDate: !m.dc ? toDay() : '' } : m) }));
  const delMed    = id => patch(p => ({ ...p, medsList: (p.medsList || []).filter(m => m.id !== id) }));

  // Antibiotic day-counter: days since startDate + 1
  const abxDay = startDate => {
    if (!startDate) return null;
    const diff = Math.floor((Date.now() - new Date(startDate)) / 86400000) + 1;
    return diff > 0 ? diff : 1;
  };

  // IV Fluids
  const addIV      = () => patch(p => ({ ...p, ivList: [...(p.ivList || []), { id: `iv${Date.now()}`, fluid: '', rate: '', duration: '', addedAt: Date.now(), dc: false, dcDate: '', dcNote: '' }] }));
  const editIV     = (id, f, v) => patch(p => ({ ...p, ivList: (p.ivList || []).map(iv => iv.id === id ? { ...iv, [f]: v } : iv) }));
  const toggleIVDC = id => patch(p => ({ ...p, ivList: (p.ivList || []).map(iv => iv.id === id ? { ...iv, dc: !iv.dc, dcDate: !iv.dc ? toDay() : '' } : iv) }));
  const delIV      = id => patch(p => ({ ...p, ivList: (p.ivList || []).filter(iv => iv.id !== id) }));

  // Diet
  const addDiet      = () => patch(p => ({ ...p, dietList: [...(p.dietList || []), { id: `d${Date.now()}`, order: '', note: '', addedAt: Date.now(), dc: false, dcDate: '', dcNote: '' }] }));
  const editDiet     = (id, f, v) => patch(p => ({ ...p, dietList: (p.dietList || []).map(d => d.id === id ? { ...d, [f]: v } : d) }));
  const toggleDietDC = id => patch(p => ({ ...p, dietList: (p.dietList || []).map(d => d.id === id ? { ...d, dc: !d.dc, dcDate: !d.dc ? toDay() : '' } : d) }));
  const delDiet      = id => patch(p => ({ ...p, dietList: (p.dietList || []).filter(d => d.id !== id) }));

  return (
    <>
      {/* Allergy conflict warnings */}
      {(() => {
        const conflicts = getAllergyConflicts(cur.allergies, activeMeds);
        if (!conflicts.length) return null;
        return (
          <div style={{ background: '#2A0A0A', border: '2px solid #FF4545', borderRadius: 10, padding: '12px 14px', marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 900, color: '#FF4545', marginBottom: 6 }}>⚠ ALLERGY CONFLICT DETECTED</div>
            {conflicts.map((c, i) => (
              <div key={i} style={{ fontSize: 12, color: '#F4A0A0', marginBottom: 3 }}>
                <b>{c.med}</b> — patient has <b>{c.allergen}</b> allergy documented
              </div>
            ))}
          </div>
        );
      })()}

      {/* IV Fluids */}
      <Card s={{ border: '1px solid #1A3A4A', background: '#07141F' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
          <SLabel color="#64D2FF" mb={0}>IV Fluids</SLabel>
          <span style={{ marginLeft: 6, fontSize: 10, color: '#64D2FF', fontWeight: 700, background: '#64D2FF22', padding: '1px 6px', borderRadius: 4 }}>{activeIV.length}</span>
          <button onClick={addIV} style={{ marginLeft: 'auto', background: '#0A2A3A', color: '#64D2FF', border: '1px solid #1A4A5A', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>+ Add</button>
        </div>
        {activeIV.length === 0 && <div style={{ color: C.dim2, fontSize: 13, padding: '6px 0', textAlign: 'center' }}>No active IV fluids</div>}
        {activeIV.map(iv => (
          <div key={iv.id} style={{ borderBottom: `1px solid ${C.bg0}`, paddingBottom: 8, marginBottom: 8 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr', gap: 6, marginBottom: 4 }}>
              <div><div style={{ fontSize: 8, color: '#64D2FF88', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Fluid</div><ICell value={iv.fluid}    onChange={v => editIV(iv.id, 'fluid',    v)} ph="0.9% NaCl / LR / D5W" /></div>
              <div><div style={{ fontSize: 8, color: '#64D2FF88', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Rate</div><ICell value={iv.rate}     onChange={v => editIV(iv.id, 'rate',     v)} ph="125 mL/hr" /></div>
              <div><div style={{ fontSize: 8, color: '#64D2FF88', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Duration</div><ICell value={iv.duration} onChange={v => editIV(iv.id, 'duration', v)} ph="continuous / 4h" /></div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {iv.addedAt && <span style={{ fontSize: 10, color: C.dim2 }}>Added {new Date(iv.addedAt).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}</span>}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                <button onClick={() => toggleIVDC(iv.id)} style={{ fontSize: 10, fontWeight: 700, background: '#FF950022', color: '#FF9500', border: '1px solid #FF950044', borderRadius: 6, padding: '3px 8px', cursor: 'pointer' }}>DC</button>
                <button onClick={() => delIV(iv.id)} style={{ background: 'none', border: 'none', color: '#FF4545', cursor: 'pointer', fontSize: 14 }}>×</button>
              </div>
            </div>
          </div>
        ))}
        {dcIV.length > 0 && (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.bg0}` }}>
            <div style={{ fontSize: 9, color: '#4A6A88', fontWeight: 800, textTransform: 'uppercase', marginBottom: 6 }}>Discontinued IVF</div>
            {dcIV.map(iv => (
              <div key={iv.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, padding: '4px 0', borderBottom: `1px solid ${C.bg0}` }}>
                <span style={{ fontSize: 12, color: '#4A6A88', textDecoration: 'line-through', flex: 1 }}>{iv.fluid} {iv.rate && `@ ${iv.rate}`}</span>
                {iv.dcDate && <span style={{ fontSize: 10, color: '#4A6A88', flexShrink: 0 }}>DC'd {fmtDay(iv.dcDate)}</span>}
                <button onClick={() => toggleIVDC(iv.id)} style={{ fontSize: 9, fontWeight: 700, background: '#30D15822', color: '#30D158', border: '1px solid #30D15844', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', flexShrink: 0 }}>Reactivate</button>
                <button onClick={() => delIV(iv.id)} style={{ background: 'none', border: 'none', color: '#FF4545', cursor: 'pointer', fontSize: 14 }}>×</button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Diet Orders */}
      <Card s={{ border: '1px solid #2A3A1A', background: '#0A1508' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
          <SLabel color="#A8D878" mb={0}>Diet</SLabel>
          <span style={{ marginLeft: 6, fontSize: 10, color: '#A8D878', fontWeight: 700, background: '#A8D87822', padding: '1px 6px', borderRadius: 4 }}>{activeDiet.length}</span>
          <button onClick={addDiet} style={{ marginLeft: 'auto', background: '#162A08', color: '#A8D878', border: '1px solid #2A4A14', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>+ Add</button>
        </div>
        {activeDiet.length === 0 && <div style={{ color: C.dim2, fontSize: 13, padding: '6px 0', textAlign: 'center' }}>No diet orders</div>}
        {activeDiet.map(d => (
          <div key={d.id} style={{ borderBottom: `1px solid ${C.bg0}`, paddingBottom: 8, marginBottom: 8 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 4 }}>
              <div>
                <div style={{ fontSize: 8, color: '#A8D87888', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Diet Order</div>
                <ICell value={d.order} onChange={v => editDiet(d.id, 'order', v)} ph="NPO / Low fat / Renal / Diabetic…" />
              </div>
              <div>
                <div style={{ fontSize: 8, color: '#A8D87888', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Note</div>
                <ICell value={d.note} onChange={v => editDiet(d.id, 'note', v)} ph="e.g. soft texture, 1.5L restrict" />
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {d.addedAt && <span style={{ fontSize: 10, color: C.dim2 }}>Added {new Date(d.addedAt).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}</span>}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                <button onClick={() => toggleDietDC(d.id)} style={{ fontSize: 10, fontWeight: 700, background: '#FF950022', color: '#FF9500', border: '1px solid #FF950044', borderRadius: 6, padding: '3px 8px', cursor: 'pointer' }}>DC</button>
                <button onClick={() => delDiet(d.id)} style={{ background: 'none', border: 'none', color: '#FF4545', cursor: 'pointer', fontSize: 14 }}>×</button>
              </div>
            </div>
          </div>
        ))}
        {dcDiet.length > 0 && (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.bg0}` }}>
            <div style={{ fontSize: 9, color: '#4A6A44', fontWeight: 800, textTransform: 'uppercase', marginBottom: 6 }}>Discontinued Diet Orders</div>
            {dcDiet.map(d => (
              <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, padding: '4px 0', borderBottom: `1px solid ${C.bg0}` }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 12, color: '#4A6A44', textDecoration: 'line-through' }}>{d.order || 'Diet order'}</span>
                  {d.note && <span style={{ fontSize: 11, color: '#4A6A44', marginLeft: 6 }}>— {d.note}</span>}
                  {d.dcDate && <div style={{ fontSize: 10, color: '#4A6A44' }}>DC'd {fmtDay(d.dcDate)}{d.dcNote ? ` · ${d.dcNote}` : ''}</div>}
                </div>
                <button onClick={() => toggleDietDC(d.id)} style={{ fontSize: 9, fontWeight: 700, background: '#30D15822', color: '#30D158', border: '1px solid #30D15844', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', flexShrink: 0 }}>Reactivate</button>
                <button onClick={() => delDiet(d.id)} style={{ background: 'none', border: 'none', color: '#FF4545', cursor: 'pointer', fontSize: 14 }}>×</button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Active Medications */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
          <SLabel mb={0}>Active Medications</SLabel>
          <span style={{ marginLeft: 6, fontSize: 10, color: '#30D158', fontWeight: 700, background: '#30D15822', padding: '1px 6px', borderRadius: 4 }}>{activeMeds.length}</span>
          <button onClick={addMed} style={{ marginLeft: 'auto', background: '#1A3D6A', color: '#7DC4FF', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>+ Add</button>
        </div>
        {activeMeds.length === 0 && <div style={{ color: C.dim2, fontSize: 13, padding: '6px 0', textAlign: 'center' }}>No active medications</div>}
        {activeMeds.map(m => (
          <div key={m.id} style={{ borderBottom: `1px solid ${C.bg0}`, paddingBottom: 8, marginBottom: 8 }}>
            {/* Row 1: Drug / Dose / Route / Freq */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 0.7fr 0.6fr 0.7fr', gap: 6, marginBottom: 4 }}>
              <div><div style={{ fontSize: 8, color: C.dim2, fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Drug</div><ICell value={m.name}  onChange={v => editMed(m.id, 'name',  v)} ph="Drug name" /></div>
              <div><div style={{ fontSize: 8, color: C.dim2, fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Dose</div><ICell value={m.dose}  onChange={v => editMed(m.id, 'dose',  v)} ph="500mg" /></div>
              <div><div style={{ fontSize: 8, color: C.dim2, fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Route</div><ICell value={m.route} onChange={v => editMed(m.id, 'route', v)} ph="IV/PO" /></div>
              <div><div style={{ fontSize: 8, color: C.dim2, fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Freq</div><ICell value={m.freq}  onChange={v => editMed(m.id, 'freq',  v)} ph="q8h" /></div>
            </div>
            {/* Row 2: Added date · duration counter · actions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              {/* Added date */}
              {m.addedAt && <span style={{ fontSize: 10, color: C.dim2 }}>Added {new Date(m.addedAt).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}</span>}
              {/* Duration: show "Duration" button only if no total set yet; once set show editable badge */}
              {!m.isAbx && !m.abxTotal && (
                <button onClick={() => editMed(m.id, 'isAbx', true)}
                  style={{ fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 6, cursor: 'pointer', border: `1px solid ${C.bd}`, background: C.bg3, color: C.dim2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  + Duration
                </button>
              )}
              {/* Input shown when toggled but total not yet entered */}
              {m.isAbx && !m.abxTotal && (
                <input autoFocus value={m.abxTotal || ''} onChange={e => editMed(m.id, 'abxTotal', e.target.value)}
                  onBlur={() => { if (!m.abxTotal) editMed(m.id, 'isAbx', false); }}
                  placeholder="days" maxLength={4}
                  style={{ width: 56, background: C.bg3, border: `1px solid #FFD60A44`, borderRadius: 6, padding: '3px 6px', color: '#FFD60A', fontSize: 11, outline: 'none' }} />
              )}
              {/* Once total is set: show editable D-counter badge */}
              {m.abxTotal && m.addedAt && (() => {
                const day   = abxDay(m.startDate || new Date(m.addedAt).toISOString().slice(0, 10));
                const total = parseInt(m.abxTotal, 10);
                const over  = day > total;
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <span style={{ fontSize: 11, fontWeight: 800, color: over ? '#FF4545' : '#FFD60A', background: over ? '#FF454522' : '#FFD60A22', border: `1px solid ${over ? '#FF454544' : '#FFD60A44'}`, borderRadius: '6px 0 0 6px', padding: '2px 8px' }}>
                      D{day}/{total}d
                    </span>
                    <input value={m.abxTotal} onChange={e => editMed(m.id, 'abxTotal', e.target.value)} maxLength={4}
                      title="Edit total days"
                      style={{ width: 36, background: over ? '#FF454511' : '#FFD60A11', border: `1px solid ${over ? '#FF454544' : '#FFD60A44'}`, borderLeft: 'none', borderRadius: '0 6px 6px 0', padding: '2px 5px', color: over ? '#FF4545' : '#FFD60A', fontSize: 10, outline: 'none', textAlign: 'center' }} />
                  </div>
                );
              })()}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                <button onClick={() => toggleDC(m.id)} style={{ fontSize: 10, fontWeight: 700, background: '#FF950022', color: '#FF9500', border: '1px solid #FF950044', borderRadius: 6, padding: '3px 8px', cursor: 'pointer' }}>DC</button>
                <button onClick={() => delMed(m.id)} style={{ background: 'none', border: 'none', color: '#FF4545', cursor: 'pointer', fontSize: 14 }}>×</button>
              </div>
            </div>
          </div>
        ))}
      </Card>

      {/* Discontinued Medications */}
      {dcMeds.length > 0 && (
        <Card s={{ background: '#0E1520', border: `1px solid ${C.bd}` }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
            <SLabel color="#4A6A88" mb={0}>Discontinued Meds</SLabel>
            <span style={{ marginLeft: 6, fontSize: 10, color: '#4A6A88', fontWeight: 700, background: '#4A6A8822', padding: '1px 6px', borderRadius: 4 }}>{dcMeds.length}</span>
          </div>
          {dcMeds.map(m => (
            <div key={m.id} style={{ borderBottom: `1px solid ${C.bg0}`, paddingBottom: 8, marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 3 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#6A8AAA', textDecoration: 'line-through' }}>{m.name}</span>
                {m.dose  && <span style={{ fontSize: 11, color: C.dim2 }}>{m.dose}</span>}
                {m.route && <span style={{ fontSize: 11, color: C.dim2 }}>{m.route}</span>}
                {m.freq  && <span style={{ fontSize: 11, color: C.dim2 }}>{m.freq}</span>}
                {m.isAbx && <span style={{ fontSize: 9, fontWeight: 800, color: '#FFD60A88', background: '#FFD60A11', border: '1px solid #FFD60A33', borderRadius: 4, padding: '1px 5px' }}>ABX{m.abxTotal ? ` /${m.abxTotal}d` : ''}</span>}
                <button onClick={() => delMed(m.id)} style={{ background: 'none', border: 'none', color: '#FF4545', cursor: 'pointer', fontSize: 14, marginLeft: 'auto' }}>×</button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {m.startDate && <span style={{ fontSize: 10, color: '#4A6A88' }}>Started {fmtDay(m.startDate)}</span>}
                {m.dcDate && <span style={{ fontSize: 10, color: '#4A6A88' }}>→ DC'd {fmtDay(m.dcDate)}</span>}
                <ICell value={m.dcNote} onChange={v => editMed(m.id, 'dcNote', v)} ph="Reason…" />
                <button onClick={() => toggleDC(m.id)} style={{ fontSize: 9, fontWeight: 700, background: '#30D15822', color: '#30D158', border: '1px solid #30D15844', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', flexShrink: 0 }}>Reactivate</button>
              </div>
            </div>
          ))}
        </Card>
      )}

      {/* Allergies */}
      <Card s={{ background: '#1A0A0A', border: '1px solid #3A1515' }}>
        <SLabel color="#FF4545">Allergies</SLabel>
        <textarea value={cur.allergies || ''} onChange={e => setFld('allergies', e.target.value)} rows={2}
          style={{ width: '100%', background: 'transparent', border: 'none', color: '#F4A0A0', fontSize: 13, resize: 'vertical' }} />
      </Card>
    </>
  );
}
